"use client";

import type { PlaceholderValue } from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import type { VariantProps } from "class-variance-authority";
import type { HeadingFont } from "./types";

import { PortableText } from "@portabletext/react";
import { useMemo } from "react";
import { cn } from "../../utils/classnames";
import { filterHiddenPlaceholderBlocks } from "../../utils/filter-empty-blocks";
import { richTextVariants } from "../../utils/rich-text-variants";
import { clampListItemLevels } from "./clamp-list-item-levels";
import { createRichTextComponents } from "./create-rich-text-components";
import {
  hasContainerWidthRichTextBlock,
  RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS,
} from "./has-container-width-rich-text-block";

type RichTextProps = {
  className?: string;
  value: PortableTextBlock[];
  components?: Partial<PortableTextComponents>;
  headingFont?: HeadingFont;
  textColor?: VariantProps<typeof richTextVariants>["textColor"];
  placeholders?: Record<string, PlaceholderValue>;
  onLinkClick?: (href: string) => void;
};

/**
 * Live preview refreshes hand RichText a new placeholders object for the same
 * content. Portable Text treats a new components map as new component types, so
 * marks continuously remount. The snapshot keeps the previous object until the
 * contents change.
 */
function serializeStable(value: unknown): string {
  return JSON.stringify(sortKeys(value)) ?? "";
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sortKeys(item));
  }

  if (value === null || typeof value !== "object") {
    return value;
  }

  const sorted: Record<string, unknown> = {};

  for (const key of Object.keys(value).sort()) {
    sorted[key] = sortKeys((value as Record<string, unknown>)[key]);
  }

  return sorted;
}

function RichText({
  className,
  value,
  components,
  headingFont,
  textColor,
  placeholders = {},
  onLinkClick,
}: RichTextProps) {
  const placeholdersSnapshotKey = serializeStable(placeholders);
  const stablePlaceholders = useMemo(
    () => placeholders,
    // placeholders omitted; snapshotKey is a stable serialization of that map.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value-stable via snapshotKey
    [placeholdersSnapshotKey],
  );
  const mergedComponents = useMemo(
    () =>
      createRichTextComponents({
        components,
        headingFont,
        onLinkClick,
        placeholders: stablePlaceholders,
      }),
    [components, headingFont, onLinkClick, stablePlaceholders],
  );

  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const filteredValue = clampListItemLevels(
    filterHiddenPlaceholderBlocks(value, stablePlaceholders),
  );

  if (!filteredValue.length) {
    return null;
  }

  const needsContainerWidthGrid = hasContainerWidthRichTextBlock(
    filteredValue,
    { placeholders: stablePlaceholders },
  );

  return (
    <div
      className={cn(
        richTextVariants({ textColor }),
        needsContainerWidthGrid
          ? RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS
          : "max-w-prose",
        className,
      )}
    >
      <PortableText components={mergedComponents} value={filteredValue} />
    </div>
  );
}

export { RichText };
